import { useParams } from "react-router"
import { useTranslation } from "react-i18next"
import { useCallback } from "react"
import { Lock } from "@phosphor-icons/react"
import {
  SelectItemWithIcon,
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
  SelectTrigger,
} from "@repo/ui"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { TooltipInfo, TooltipProvider } from "@/components/ui/tooltip.tsx"
import { Switch } from "@/components/ui/switch.tsx"
import { ScrollableRouteContainer } from "@/components/shared/Container.tsx"
import { PageBody, PageHeader } from "@/components/shared/page-sections.tsx"
import { EcommerceCommonHeader } from "@/components/shared/headers.tsx"
import { useGetDeatiledSingleFinancialModelQuery } from "@/feature/financial-model/api"
import {
  useGetDirectLabourSettingData,
  usePostDirectLabourSettingDataMutation,
} from "@/feature/financial-model/ecommerce/cogs/api"
import { DirectLabourModelingApproachSetting } from "@/feature/financial-model/ecommerce/cogs/api/types.ts"

const CogsSetupDirectLabourRoute = () => {
  const { id: financialModelId } = useParams<{ id: string }>()
  if (!financialModelId) {
    throw new Error(
      `CogsSetupDirectLabourRoute > financialModelId is undefined`
    )
  }
  const { t } = useTranslation(`cogs`)
  const { financialModel } =
    useGetDeatiledSingleFinancialModelQuery(financialModelId)

  const { data: settings } = useGetDirectLabourSettingData(financialModelId)

  const { mutate: updateSettings } =
    usePostDirectLabourSettingDataMutation(financialModelId)

  const toggleIsManagedInCogsActive = useCallback(() => {
    updateSettings({
      financial_model_id: financialModelId,
      active: !settings.active,
      modeling_approach: settings.modeling_approach,
    })
  }, [
    financialModelId,
    settings.active,
    settings.modeling_approach,
    updateSettings,
  ])
  const handleSelectModelingApproach = useCallback(
    (value: DirectLabourModelingApproachSetting) => {
      updateSettings({
        financial_model_id: financialModelId,
        active: settings.active,
        modeling_approach: value,
      })
    },
    [financialModelId, settings.active, updateSettings]
  )

  return (
    <ScrollableRouteContainer className={`p-0`}>
      <TooltipProvider>
        <PageHeader>
          <EcommerceCommonHeader
            linkText={t(`cogs.set_up.direct_labour.header.back`)}
            path={`/dashboard/financial-model/${financialModelId}/ecommerce/cogs`}
          />
          <div className="mt-6 flex justify-between items-center">
            <div>
              <h1 className="page-title">
                {t(`cogs.set_up.direct_labour.header.title`)}
                {` - `}
                <span className="page-title-secondary-content">
                  {t(`cogs.set_up.direct_labour.header.subtitle`)}
                </span>
              </h1>
              <p className="page-subtitle">
                {t(`cogs.home.header.subtitle`)} | {financialModel?.data?.name}
              </p>
            </div>
          </div>
        </PageHeader>
        <PageBody>
          <div className={`flex flex-col gap-6`}>
            <div
              className={`bg-white rounded-lg p-6 shadow-sm flex w-full justify-between`}
            >
              <p className={`text-lg font-medium`}>
                {t(`cogs.set_up.direct_labour.model_in_feature_toggle`)}
              </p>
              <Switch
                checked={settings.active}
                onCheckedChange={toggleIsManagedInCogsActive}
              />
            </div>
            {settings.active && (
              <Accordion
                defaultValue={settings.labour_department_id}
                type="single"
                className="space-y-4"
              >
                <AccordionItem
                  value={settings.labour_department_id}
                  className="bg-white rounded-lg px-4 shadow-sm"
                >
                  <AccordionTrigger className="text-lg font-medium">
                    {t(`cogs.set_up.direct_labour.department_name`)}
                  </AccordionTrigger>
                  <AccordionContent>
                    <TooltipProvider>
                      <div className="grid grid-cols-2">
                        <div className="pr-8">
                          <div className="flex gap-2">
                            <h3 className="mb-2">
                              {t(`cogs.set_up.direct_labour.select.label`)}
                            </h3>
                            <TooltipInfo>
                              {t(`cogs.set_up.direct_labour.select.tooltip`)}
                            </TooltipInfo>
                          </div>
                          <Select
                            onValueChange={(
                              value: DirectLabourModelingApproachSetting
                            ) => {
                              handleSelectModelingApproach(value)
                            }}
                            defaultValue={
                              settings.modeling_approach ?? undefined
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select Modeling Approach" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="by_department">
                                {t(
                                  `cogs.set_up.direct_labour.select.options.fte`
                                )}
                              </SelectItem>
                              <SelectItemWithIcon
                                disabled
                                value="by_role"
                                icon={<Lock />}
                                iconPosition="right"
                              >
                                {t(
                                  `cogs.set_up.direct_labour.select.options.role`
                                )}
                              </SelectItemWithIcon>
                              <SelectItemWithIcon
                                value="by_employee"
                                icon={<Lock />}
                                iconPosition="right"
                                disabled
                              >
                                {t(
                                  `cogs.set_up.direct_labour.select.options.employee`
                                )}
                              </SelectItemWithIcon>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="pl-8 border-l border-gray-200">
                          <div className="flex items-center gap-2">
                            <h3 className="text-sm text-disabled uppercase font-semibold">
                              {t(`cogs.set_up.direct_labour.premium.title`)}
                            </h3>
                          </div>
                          <div className="flex items-center mt-1 max-w-[490px] w-full justify-between">
                            <div className="flex items-center gap-2">
                              <p className="text-sm text-disabled">
                                {t(
                                  `cogs.set_up.direct_labour.premium.options.sub_department`
                                )}
                              </p>
                              <TooltipInfo>
                                {t(
                                  `cogs.set_up.direct_labour.premium.tooltip.sub_department`
                                )}
                              </TooltipInfo>
                            </div>
                            <Switch disabled />
                          </div>
                          <div className="flex items-center mt-2 max-w-[490px] w-full justify-between">
                            <div className="flex items-center gap-2">
                              <p className="text-sm text-disabled">
                                {t(
                                  `cogs.set_up.direct_labour.premium.options.hiring`
                                )}
                              </p>
                              <TooltipInfo>
                                {t(
                                  `cogs.set_up.direct_labour.premium.tooltip.hiring`
                                )}
                              </TooltipInfo>
                            </div>
                            <Switch disabled />
                          </div>
                        </div>
                      </div>
                    </TooltipProvider>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            )}
          </div>
        </PageBody>
      </TooltipProvider>
    </ScrollableRouteContainer>
  )
}

export default CogsSetupDirectLabourRoute
